import { Teams } from './enums'
import {
  type FeatureFlagConfig,
  type FeatureFlagKey,
  type FeatureFlags,
} from './types'

/**
 * Flag defaults, split from FeatureFlag.service so that consumers of the
 * defaults (e.g. the global store reducer) don't pull the LaunchDarkly SDK
 * into their module graph. Keep this module free of runtime dependencies
 * beyond local enums/types.
 */
export const FEATURE_FLAGS_CONFIG: FeatureFlagConfig = {
  // GRANDFATHERED
  CHQ_34_ADD_AMZNPO_TAX_SHIP: [false, Teams.GRANDFATHERED],
  SY_1803_PUNCHOUT_ON_BEHALF_OF: [false, Teams.GRANDFATHERED],
  FT_5439_DISABLE_BASE_CURRENCY_CHANGE: [false, Teams.GRANDFATHERED],
  FT_4880_ENABLE_INTERCOM: [false, Teams.GRANDFATHERED],
  FT_4280_BILL_PAYMENT_CONSOLIDATION: [false, Teams.GRANDFATHERED],

  // Team AP
  FT_7515_FIX_CREATE_BILL_PERMISSION_ACCESS: [false, Teams.TEAM_AP],
  AP_10029_UNBILLED_ITEMS_PAGE_MIGRATION: [false, Teams.TEAM_AP],
  AP_10424_SIDE_BY_SIDE_VIEW: [false, Teams.TEAM_AP],
  AP_10538_INVOICE_INTAKE_BY_UPLOAD: [false, Teams.TEAM_AP],
  AP_10569_BILL_ITEM_NEGATIVE_TAX: [false, Teams.TEAM_AP],
  AP_10719_PDF_IFRAME_EMBED: [false, Teams.TEAM_AP],

  // Team CLC
  CL_1324_NEW_NO_PERMISSION_PAGE: [false, Teams.TEAM_CLC],

  // Team Pay
  PAY_24_CANADIAN_SPENDING_CARDS: [false, Teams.TEAM_PAYMENTS],
  PAY_4551_DISCOVER_MORE_PAGE: [false, Teams.TEAM_PAYMENTS],
  PAY_4205_AP_PROMO_DASHBOARD_WIDGET: [false, Teams.TEAM_PAYMENTS],
  PAY_4248_BILL_PAY_NAVIGATION_PROMO: [false, Teams.TEAM_PAYMENTS],
  PAY_4731_AP_NAVIGATION_PROMO: [false, Teams.TEAM_PAYMENTS],
  PAY_4732_CONTRACTS_NAVIGATION_PROMO: [false, Teams.TEAM_PAYMENTS],
  PAY_4194_FRAUD_FEEDBACK: [false, Teams.TEAM_PAYMENTS],

  // Team Integrations
  INT_1976_MIGRATE_MANAGE_USERS_AND_PERMISSIONS: [
    false,
    Teams.TEAM_INTEGRATIONS,
  ],

  // Team Platform
  PLAT_2155_MERGE_NETSUITE: [false, Teams.TEAM_PLATFORM],
  PLAT_4870_MULTI_ENTITY: [false, Teams.TEAM_PLATFORM],
  PLAT_4199_MDS_FORCE_RESYNC: [false, Teams.TEAM_PLATFORM],
  PLAT_5164_MERGE_SAGE: [false, Teams.TEAM_PLATFORM],
  PLAT_5595_MERGE_BUSINESS_CENTRAL: [false, Teams.TEAM_PLATFORM],
  PLAT_5726_HEADER_MEMO_CUSTOM_FIELD_MAPPING: [false, Teams.TEAM_PLATFORM],

  // Team Purchasing
  CT_700_CREATE_PO_SHIPPING_ADDRESS_USE_REQUESTER_NAME: [
    false,
    Teams.TEAM_PURCHASING,
  ],
  RFO_AUTOMATION: [false, Teams.TEAM_PURCHASING],
  PUR_3723_SEQUENTIAL_ORDER_NUMBER: [false, Teams.TEAM_PURCHASING],
  PUR_3524_NEXT_GEN_PUNCHOUTS: [false, Teams.TEAM_PURCHASING],
  PUR_4239_AI_ACCOUNT_CODE_FOR_PUNCHOUT_ITEMS: [false, Teams.TEAM_PURCHASING],
  PUR_4306_AI_ACCOUNT_CODE_BACKGROUND_UPDATE: [false, Teams.TEAM_PURCHASING],
  FT_5523_DISABLE_PUNCHOUT_ORDER_BULK_APPROVE: [false, Teams.TEAM_PURCHASING],
  PUR_4444_PUNCHOUT_ORDER_REQUEST_CONTACT: [false, Teams.TEAM_PURCHASING],
  PUR_4706_AUTO_PO_BILL_TO: [false, Teams.TEAM_PURCHASING],
  PUR_3184_NGI_CATALOG: [false, Teams.TEAM_PURCHASING],

  // Team Data
  FT_2274_ENABLE_SPEND_INSIGHTS_DASHBOARD: [false, Teams.TEAM_DATA],
  DATA_380_ENABLE_SPEND_ANALYTICS_SALES_DEMO: [false, Teams.TEAM_DATA],
  DATA_ENABLE_SPEND_INSIGHTS_BETA_FEATURES: [false, Teams.TEAM_DATA],
  DATA_1808_ENABLE_THOUGHTSPOT_BUDGET: [false, Teams.TEAM_DATA],
  DATA_1439_GLOBAL_SEARCH: [false, Teams.TEAM_DATA],
  DATA_2061_GUIDED_INTAKE_ORDERS: [false, Teams.TEAM_DATA],
  DATA_3299_AMAZON_BUSINESS_CONNECT: [false, Teams.TEAM_DATA],
  DATA_3467_PO_BILL_VISIBILITY: [false, Teams.TEAM_DATA],

  // Team Architecture
  ARCH_468_MIGRATION_SETTINGS_UNIT: [false, Teams.TEAM_ARCHITECTURE],
  ARCH_514_MIGRATION_EXPENSE_APPROVALS: [false, Teams.TEAM_ARCHITECTURE],
  ARCH_531_MIGRATION_EXPENSE_DETAILS: [false, Teams.TEAM_ARCHITECTURE],
  ARCH_544_MIGRATION_EXPENSE_REQUEST: [false, Teams.TEAM_ARCHITECTURE],
  ARCH_615_MIGRATION_CATALOG_ACCESS: [false, Teams.TEAM_ARCHITECTURE],

  // Team PServ
  PSERV_255_DATEFORMAT_MIGRATION: [false, Teams.TEAM_PSERV],
  PSERV_346_BASE_MIGRATION: [false, Teams.TEAM_PSERV],

  // Team Design System
  XP_78_LIST_COUNT_SUMMARY: [false, Teams.DESIGN_SYSTEM],

  // Team Angular0 (PSERV-480) - temporary page-polish flags, retire together
  AP_10701_BILL_APPROVAL_STATUS_POLISH: [false, Teams.TEAM_ANGULAR0],
  AP_10702_UNBILLED_ORDER_ITEMS_POLISH: [false, Teams.TEAM_ANGULAR0],
  AP_10703_EXPENSE_UNBILLED_ITEMS_POLISH: [false, Teams.TEAM_ANGULAR0],
  AP_10704_CC_RECONCILIATION_LIST_POLISH: [false, Teams.TEAM_ANGULAR0],
  AP_10705_CC_RECONCILIATION_DETAIL_POLISH: [false, Teams.TEAM_ANGULAR0],
  AP_10706_CC_RECONCILIATION_MATCHING_POLISH: [false, Teams.TEAM_ANGULAR0],
  AP_10707_ACCRUALS_POLISH: [false, Teams.TEAM_ANGULAR0],
  AP_10708_CUSTOMIZE_UNIT_TYPES_POLISH: [false, Teams.TEAM_ANGULAR0],
  AP_10709_MANAGEMENT_CHART_OF_ACCOUNTS_POLISH: [false, Teams.TEAM_ANGULAR0],
  AP_10710_ACCOUNT_CODE_CORRECTION_POLISH: [false, Teams.TEAM_ANGULAR0],
  AP_10711_MANAGE_CURRENCY_RATES_POLISH: [false, Teams.TEAM_ANGULAR0],
  AP_10712_MANAGE_TAX_TYPES_POLISH: [false, Teams.TEAM_ANGULAR0],
  AP_10713_CLOSING_MONTHS_POLISH: [false, Teams.TEAM_ANGULAR0],
  PAY_4498_CUSTOMIZE_REQUEST_FOR_EXPENSE_FORM_POLISH: [
    false,
    Teams.TEAM_ANGULAR0,
  ],
  PAY_4499_MANAGE_BANK_CREDIT_CARD_ACCOUNTS_POLISH: [
    false,
    Teams.TEAM_ANGULAR0,
  ],
  PLAT_5518_QUICKBOOKS_DESKTOP_POLISH: [false, Teams.TEAM_ANGULAR0],
  PLAT_5519_ADMIN_TOOLS_POLISH: [false, Teams.TEAM_ANGULAR0],
  PUR_4518_TRAVEL_STATUS_POLISH: [false, Teams.TEAM_ANGULAR0],
  PUR_4519_TRAVEL_DETAIL_POLISH: [false, Teams.TEAM_ANGULAR0],
  PUR_4520_TRAVEL_REQUEST_POLISH: [false, Teams.TEAM_ANGULAR0],
  PUR_4521_ORDER_APPROVAL_STATUS_POLISH: [false, Teams.TEAM_ANGULAR0],
  PUR_4522_TRAVEL_APPROVAL_STATUS_POLISH: [false, Teams.TEAM_ANGULAR0],
  PUR_4523_TRAVEL_APPROVAL_DETAIL_POLISH: [false, Teams.TEAM_ANGULAR0],
  PUR_4524_CUSTOMIZE_REQUEST_FOR_ORDER_FORM_POLISH: [
    false,
    Teams.TEAM_ANGULAR0,
  ],
  PUR_4525_CUSTOMIZE_PURCHASE_ORDER_FORM_POLISH: [false, Teams.TEAM_ANGULAR0],
  PUR_4526_MANAGE_BUDGETS_POLISH: [false, Teams.TEAM_ANGULAR0],
  DATA_3329_OVERALL_SPEND_POLISH: [false, Teams.TEAM_ANGULAR0],
  DATA_3330_EXPENSE_SPEND_REPORT_POLISH: [false, Teams.TEAM_ANGULAR0],
  DATA_3331_RECEIVING_SPEND_REPORT_POLISH: [false, Teams.TEAM_ANGULAR0],
  DATA_3332_ORDERS_BY_VENDOR_POLISH: [false, Teams.TEAM_ANGULAR0],
  DATA_3333_ORDERS_BY_USER_POLISH: [false, Teams.TEAM_ANGULAR0],
  DATA_3334_ORDERS_DEPARTMENT_POLISH: [false, Teams.TEAM_ANGULAR0],
  DATA_3335_EXPENSE_BY_DEPARTMENT_POLISH: [false, Teams.TEAM_ANGULAR0],
  DATA_3336_REPORTS_ACCESS_POLISH: [false, Teams.TEAM_ANGULAR0],
  DATA_3337_EXPORT_DATA_POLISH: [false, Teams.TEAM_ANGULAR0],
}

export const FEATURE_FLAG_DEFAULTS = Object.keys(FEATURE_FLAGS_CONFIG).reduce(
  (obj, key) => {
    const [defaultValue] = FEATURE_FLAGS_CONFIG[key as FeatureFlagKey]

    return {
      [key]: defaultValue,
      ...obj,
    }
  },
  {}
) as FeatureFlags
